/**
 * TrendText — a month-on-month change in colour (SF-5117): green for up, red for down, grey for
 * no change or nothing to compare with. With a caption it reads as a sentence under a tile
 * ("+12% vs last month"); without one it is a table cell ("+12%").
 */
import { Typography, theme } from 'antd';
import { EM_DASH } from '../../utils/format';
import { formatChange, trendOf } from './usageProgress';

const { Text } = Typography;

export interface TrendTextProps {
  current: number | null | undefined;
  previous: number | null | undefined;
  /** E.g. "vs last month" or "vs same days last month". */
  caption?: string;
  /** Whether a rise is good news (green). Defaults to true: more feedback is good. */
  higherIsBetter?: boolean;
}

export const TrendText: React.FC<TrendTextProps> = ({
  current,
  previous,
  caption,
  higherIsBetter = true,
}) => {
  const { token } = theme.useToken();
  const trend = trendOf(current, previous);
  let text: string;
  let color = token.colorTextSecondary;
  if (trend.kind === 'none') {
    text = caption ? 'no data last month' : EM_DASH;
  } else if (trend.kind === 'flat') {
    text = 'no change';
  } else {
    const good = (trend.kind === 'up') === higherIsBetter;
    color = good ? token.colorSuccess : token.colorError;
    text = `${formatChange(trend.pct as number)}${caption ? ` ${caption}` : ''}`;
  }
  return (
    <Text style={{ color }} data-trend={trend.kind}>
      {text}
    </Text>
  );
};
